/**
 * MeetingCountdown
 * Fullscreen intro-slide synced across all participants via Firestore.
 * Host starts/stops the countdown; all attendees see the same timer,
 * background image, and hear the same YouTube/audio music.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { db, storage } from '../../../firebase';
import { doc, setDoc, onSnapshot, serverTimestamp, collection, getDocs, addDoc, deleteDoc, query, orderBy, where, updateDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  COLLECTIONS,
  COUNTDOWN_DEFAULT_MINUTES,
  STRINGS,
} from '../meetingConstants';
import PreMeetingSetupPanel from './PreMeetingSetupPanel';
import {
  ImageIcon, FilmIcon, TimeIcon, FiltersIcon,
  ClipboardIcon, SettingsIcon as SettingsNavIcon,
  DocumentIcon, ConnectionLinkIcon, MicrophoneIcon, GoalIcon, NotificationIcon, VideoIcon,
} from '../../icons/phosphorSystem';
import { MusicNote } from 'phosphor-react';

// ── YouTube helpers ─────────────────────────────────────────────────────────

function extractYouTubeId(url: string): string | null {
  const m =
    url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/) ??
    url.match(/^([A-Za-z0-9_-]{11})$/);
  return m?.[1] ?? null;
}

function extractYouTubePlaylistId(url: string): string | null {
  const m = url.match(/[?&]list=([A-Za-z0-9_-]+)/);
  return m?.[1] ?? null;
}

function buildYouTubeEmbedUrl(url: string): string | null {
  const playlistId = extractYouTubePlaylistId(url);
  const videoId = extractYouTubeId(url);
  if (playlistId) {
    const startVideo = videoId ? `&v=${videoId}` : '';
    return `https://www.youtube.com/embed/videoseries?list=${playlistId}${startVideo}&autoplay=1&loop=1&enablejsapi=1&controls=0`;
  }
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&enablejsapi=1&controls=0`;
  }
  return null;
}

function detectYouTubeType(url: string): 'playlist' | 'video' | null {
  if (extractYouTubePlaylistId(url)) return 'playlist';
  if (extractYouTubeId(url)) return 'video';
  return null;
}

// ── Mini analog clock SVG ────────────────────────────────────────────────────

function MiniAnalogClock({ tz, nowTick, size = 28, opacity = 0.5, accent }: { tz: string; nowTick: number; size?: number; opacity?: number; accent?: string }) {
  let h = 0, m = 0;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: 'numeric', hour12: false, timeZone: tz }).formatToParts(new Date(nowTick));
    h = Number(parts.find(p => p.type === 'hour')?.value ?? 0);
    m = Number(parts.find(p => p.type === 'minute')?.value ?? 0);
  } catch { /* fallback 0:00 */ }
  const hAngle = ((h % 12) * 30 + m * 0.5 - 90) * Math.PI / 180;
  const mAngle = (m * 6 - 90) * Math.PI / 180;
  const col = accent || 'white';
  return (
    <svg viewBox="0 0 40 40" style={{ width: size, height: size, opacity }}>
      <circle cx="20" cy="20" r="18" fill="none" stroke="white" strokeWidth="1.2" opacity="0.3" />
      {[0,1,2,3,4,5,6,7,8,9,10,11].map((n) => {
        const a = (n * 30 - 90) * Math.PI / 180;
        return <circle key={n} cx={20 + Math.cos(a) * 15} cy={20 + Math.sin(a) * 15} r={n % 3 === 0 ? 1.2 : 0.6} fill="white" opacity="0.4" />;
      })}
      <line x1="20" y1="20" x2={20 + Math.cos(hAngle) * 9} y2={20 + Math.sin(hAngle) * 9} stroke={col} strokeWidth="1.8" strokeLinecap="round" opacity="0.85" />
      <line x1="20" y1="20" x2={20 + Math.cos(mAngle) * 13} y2={20 + Math.sin(mAngle) * 13} stroke={col} strokeWidth="1" strokeLinecap="round" opacity="0.65" />
      <circle cx="20" cy="20" r="1.2" fill={col} opacity="0.85" />
    </svg>
  );
}

// ── Props ───────────────────────────────────────────────────────────────────

interface FeatureToggles {
  docView: boolean;
  linkOpener: boolean;
  guestSpotlight: boolean;
  commitmentReview: boolean;
  slideshow: boolean;
  taskReminder: boolean;
  autoRecord: boolean;
}

/** Sessietemplate-editor (zelfde als hub) — alleen host, vanuit NetworkClubMeeting */
export interface SessionTemplateEditorContext {
  userId: string;
  entityType: 'mastermind' | 'community' | 'networkclub';
  memberCount: number;
  memberNames: string[];
  memberAvatars?: (string | null)[];
}

interface MeetingCountdownProps {
  entityId: string;
  clubName?: string;
  logoUrl?: string | null;
  musicUrl?: string | null;
  videoUrl?: string | null;
  imageUrl?: string | null;
  slides?: { url: string; type: 'image' | 'video'; caption?: string }[];
  countdownMinutes?: number;
  primaryColor?: string;
  isHost: boolean;
  featureToggles?: FeatureToggles;
  onFeatureToggle?: (key: keyof FeatureToggles, val: boolean) => void;
  /** Host: toont tab “Sessie” met template/blokken-editor */
  sessionTemplateEditor?: SessionTemplateEditorContext | null;
  /** Direct de instellingen openen op een specifiek tabblad */
  openSettings?: 'commitments' | 'achtergrond' | 'muziek' | 'slides' | 'features' | 'timer' | 'widgets' | 'sessie';
  /**
   * Standaard gelijk aan `isHost`. Zet op false wanneer iemand wel het instellingenpaneel (bv. alleen tab Sessie) mag zien maar geen countdown mag starten/stoppen.
   */
  canOperateCountdown?: boolean;
  /** Alleen tab “Meeting instellingen” (sessietemplate) in het zijmenu */
  restrictSettingsToSessionOnly?: boolean;
  startLabel?: string;
  onMeetingStart: () => void;
  onClose: () => void;
}

type CountdownState = 'idle' | 'running' | 'finished';

const MeetingCountdown: React.FC<MeetingCountdownProps> = ({
  entityId,
  clubName,
  logoUrl,
  musicUrl: initialMusicUrl,
  videoUrl,
  imageUrl: initialImageUrl,
  slides,
  countdownMinutes = COUNTDOWN_DEFAULT_MINUTES,
  primaryColor = '#7A9488',
  isHost,
  featureToggles,
  onFeatureToggle,
  sessionTemplateEditor,
  openSettings,
  canOperateCountdown,
  restrictSettingsToSessionOnly = false,
  onMeetingStart,
  onClose,
  startLabel,
}: MeetingCountdownProps & { startLabel?: string }) => {
  const canRunCountdown = canOperateCountdown ?? isHost;
  const [state, setState] = useState<CountdownState>('idle');
  const [timerMinutes, setTimerMinutes] = useState(countdownMinutes);
  const [remainingSeconds, setRemainingSeconds] = useState(countdownMinutes * 60);

  // Meeting time-based countdown (target time approach)
  const [meetingTimeHour, setMeetingTimeHour] = useState<number>(() => {
    const d = new Date(); d.setMinutes(d.getMinutes() + countdownMinutes);
    return d.getHours();
  });
  const [meetingTimeMinute, setMeetingTimeMinute] = useState<number>(() => {
    const d = new Date(); d.setMinutes(d.getMinutes() + countdownMinutes);
    return Math.ceil(d.getMinutes() / 5) * 5 % 60;
  });
  const [use12h, setUse12h] = useState(false);
  const [hostTimezone, setHostTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [nowTick, setNowTick] = useState(Date.now());
  const [musicError, setMusicError] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isMusicLoading, setIsMusicLoading] = useState(false);
  const [musicStarted, setMusicStarted] = useState(false);

  // ── Live clock tick (updates every second) ──────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  // ── Compute meeting target timestamp from set time ─────────────────────
  const meetingTargetMs = useMemo(() => {
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), meetingTimeHour, meetingTimeMinute, 0, 0);
    if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
    return target.getTime();
  }, [meetingTimeHour, meetingTimeMinute]);

  const autoCountdownSec = Math.max(0, Math.floor((meetingTargetMs - nowTick) / 1000));

  // ── Timezone definitions ───────────────────────────────────────────────
  const WORLD_ZONES = useMemo(() => [
    { tz: 'America/New_York',     label: 'New York',    short: 'EST' },
    { tz: 'America/Sao_Paulo',    label: 'São Paulo',   short: 'BRT' },
    { tz: 'Europe/London',        label: 'London',      short: 'GMT' },
    { tz: 'Europe/Amsterdam',     label: 'Amsterdam',   short: 'CET' },
    { tz: 'Europe/Paris',         label: 'Parijs',      short: 'CET' },
    { tz: 'Africa/Johannesburg',  label: 'Kaapstad',    short: 'SAST' },
    { tz: 'Asia/Dubai',           label: 'Dubai',       short: 'GST' },
    { tz: 'Asia/Shanghai',        label: 'Shanghai',    short: 'CST' },
    { tz: 'Asia/Tokyo',           label: 'Tokyo',       short: 'JST' },
    { tz: 'Australia/Sydney',     label: 'Sydney',      short: 'AEST' },
  ], []);

  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const formatTimeInZone = useCallback((tz: string) => {
    try {
      return new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: false }).format(new Date(nowTick));
    } catch { return '--:--'; }
  }, [nowTick]);

  const getMeetingTimeInZone = useCallback((tz: string) => {
    try {
      return new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: false }).format(new Date(meetingTargetMs));
    } catch { return '--:--'; }
  }, [meetingTargetMs]);

  // Clock zoom level
  const [clockZoom, setClockZoom] = useState(1);

  // Host editable fields
  const [localMusicUrl, setLocalMusicUrl] = useState(initialMusicUrl ?? '');
  const [localImageUrl, setLocalImageUrl] = useState(initialImageUrl ?? '');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showSettings, setShowSettings] = useState(() => !!openSettings);
  const [savingMusic, setSavingMusic] = useState(false);
  const [musicSaved, setMusicSaved] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Slideshow management
  const [localSlides, setLocalSlides] = useState<{ url: string; name: string; type: 'image' | 'video' }[]>([]);
  const [uploadingSlides, setUploadingSlides] = useState(false);
  const slideInputRef = useRef<HTMLInputElement>(null);

  // Advanced settings panel
  type BgMode = 'image' | 'slideshow' | 'youtube';
  const [bgMode, setBgMode] = useState<BgMode>(initialImageUrl ? 'image' : 'slideshow');
  const [settingsTab, setSettingsTab] = useState<'commitments' | 'achtergrond' | 'muziek' | 'slides' | 'features' | 'timer' | 'widgets' | 'sessie'>(openSettings ?? 'achtergrond');
  const [sessionTemplateSavedFlash, setSessionTemplateSavedFlash] = useState(false);

  const settingsNavItems = useMemo(() => {
    const all = [
      { key: 'commitments' as const, label: 'Commitments', Icon: GoalIcon },
      { key: 'achtergrond' as const, label: 'Achtergrond', Icon: ImageIcon },
      { key: 'muziek' as const, label: 'Muziek', Icon: MusicNote },
      { key: 'slides' as const, label: 'Slideshow', Icon: FilmIcon },
      { key: 'timer' as const, label: 'Countdown', Icon: TimeIcon },
      { key: 'widgets' as const, label: 'Widgets', Icon: FiltersIcon },
      { key: 'sessie' as const, label: 'Meeting instellingen', Icon: ClipboardIcon },
      { key: 'features' as const, label: 'Features', Icon: SettingsNavIcon },
    ];
    return restrictSettingsToSessionOnly ? all.filter(t => t.key === 'sessie') : all;
  }, [restrictSettingsToSessionOnly]);

  useEffect(() => {
    if (!restrictSettingsToSessionOnly) return;
    setSettingsTab('sessie');
    setShowSettings(true);
  }, [restrictSettingsToSessionOnly]);
  const [widgetNextMeetingEnabled, setWidgetNextMeetingEnabled] = useState(true);
  const [widgetNextMeetingSize, setWidgetNextMeetingSize] = useState<'compact' | 'large'>('compact');
  const [widgetRsvpClosingEnabled, setWidgetRsvpClosingEnabled] = useState(false);
  // Taken Reminder frequentie (opgeslagen in entitySettings)
  const [reminderDays, setReminderDays] = useState<2 | 4 | 7>(7);
  useEffect(() => {
    if (!entityId) return;
    const unsub = onSnapshot(doc(db, 'entitySettings', entityId), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data().taskReminderDays;
      if ([2, 4, 7].includes(d)) setReminderDays(d as 2 | 4 | 7);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // ── Commitments tab state ──────────────────────────────────────────────────
  const [currentUser] = useAuthState(auth);
  // ── Commitments: 6 genummerde slots ──────────────────────────────────────
  const MAX_COMMITMENT_SLOTS = 6;
  type CommitmentSlot = { id: string | null; text: string; saving: boolean; saved: boolean };
  const emptySlot = (): CommitmentSlot => ({ id: null, text: '', saving: false, saved: true });
  const [commitmentSlots, setCommitmentSlots] = useState<CommitmentSlot[]>(
    Array.from({ length: MAX_COMMITMENT_SLOTS }, emptySlot)
  );
  const [commitmentLoading, setCommitmentLoading] = useState(false);

  useEffect(() => {
    if (settingsTab !== 'commitments' || !entityId || !currentUser?.uid) return;
    setCommitmentLoading(true);
    const q = query(
      collection(db, 'structuredGrowthCommitments'),
      where('entityId', '==', entityId),
      where('userId', '==', currentUser.uid),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc'),
    );
    getDocs(q)
      .then(snap => {
        const loaded: CommitmentSlot[] = snap.docs.map(d => ({
          id: d.id,
          text: d.data().commitmentText ?? '',
          saving: false,
          saved: true,
        }));
        // Vul slots: bestaande commitments vooraan, lege slots erachter
        const slots = Array.from({ length: MAX_COMMITMENT_SLOTS }, (_, i) =>
          i < loaded.length ? loaded[i] : emptySlot()
        );
        setCommitmentSlots(slots);
      })
      .catch(() => {})
      .finally(() => setCommitmentLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsTab, entityId, currentUser?.uid]);

  const saveCommitmentSlot = useCallback(async (idx: number, slots: CommitmentSlot[]) => {
    const slot = slots[idx];
    // Niets te doen als tekst leeg en nog niet opgeslagen
    if (!slot.text.trim() && !slot.id) return;
    // Niets te doen als al opgeslagen en niet gewijzigd
    if (slot.saved) return;

    setCommitmentSlots(prev => prev.map((s, i) => i === idx ? { ...s, saving: true } : s));
    try {
      if (slot.id) {
        await updateDoc(doc(db, 'structuredGrowthCommitments', slot.id), {
          commitmentText: slot.text.trim(),
        });
        setCommitmentSlots(prev => prev.map((s, i) => i === idx ? { ...s, saving: false, saved: true } : s));
      } else if (slot.text.trim()) {
        const ref = await addDoc(collection(db, 'structuredGrowthCommitments'), {
          entityId,
          userId: currentUser!.uid,
          userName: currentUser!.displayName ?? currentUser!.email?.split('@')[0] ?? 'Gebruiker',
          meetingId: '',
          commitmentText: slot.text.trim(),
          deadline: null,
          status: 'pending',
          createdAt: serverTimestamp(),
        });
        setCommitmentSlots(prev => prev.map((s, i) => i === idx ? { ...s, id: ref.id, saving: false, saved: true } : s));
      }
    } catch {
      setCommitmentSlots(prev => prev.map((s, i) => i === idx ? { ...s, saving: false } : s));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, currentUser]);

  const deleteCommitmentSlot = useCallback(async (idx: number) => {
    const slot = commitmentSlots[idx];
    if (slot.id) {
      await updateDoc(doc(db, 'structuredGrowthCommitments', slot.id), { status: 'fulfilled' }).catch(() => {});
    }
    setCommitmentSlots(prev => prev.map((s, i) => i === idx ? emptySlot() : s));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitmentSlots]);

  // Meeting widgets (Jitsi overlay): next meeting + RSVP tijdens afsluiting
  useEffect(() => {
    if (!entityId) return;
    const unsub = onSnapshot(doc(db, COLLECTIONS.SETTINGS, entityId), (snap) => {
      if (!snap.exists()) return;
      const d = snap.data() as Record<string, unknown>;
      if (typeof d.widgetNextMeetingEnabled === 'boolean') setWidgetNextMeetingEnabled(d.widgetNextMeetingEnabled);
      if (d.widgetNextMeetingSize === 'large' || d.widgetNextMeetingSize === 'compact') {
        setWidgetNextMeetingSize(d.widgetNextMeetingSize);
      }
      if (typeof d.widgetRsvpClosingEnabled === 'boolean') setWidgetRsvpClosingEnabled(d.widgetRsvpClosingEnabled);
    });
    return () => unsub();
  }, [entityId]);
  const [previewSlideIdx, setPreviewSlideIdx] = useState(0);
  const [slideDragIdx, setSlideDragIdx] = useState<number | null>(null);
  const [ytAudioOnly, setYtAudioOnly] = useState(true);

  // Overlay customisation
  const [overlayColor1, setOverlayColor1] = useState(primaryColor || '#8DA8A0');
  const [overlayColor2, setOverlayColor2] = useState('#0f172a');
  const [overlayOpacity, setOverlayOpacity] = useState(0.85);
  const [overlayAngle, setOverlayAngle] = useState(135);

  // Slideshow templates
  interface SlideTemplate { id: string; name: string; slides: typeof localSlides; createdAt?: any }
  const [slideTemplates, setSlideTemplates] = useState<SlideTemplate[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const ytPlayerRef = useRef<HTMLIFrameElement | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Slideshow background cycling (uses localSlides from Firestore) ──────
  const [bgSlideIdx, setBgSlideIdx] = useState(0);
  const bgCycleRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (bgCycleRef.current) clearInterval(bgCycleRef.current);
    if (bgMode !== 'slideshow' || localSlides.length <= 1) return;
    bgCycleRef.current = setInterval(() => {
      setBgSlideIdx((prev) => (prev + 1) % localSlides.length);
    }, 6_000);
    return () => { if (bgCycleRef.current) clearInterval(bgCycleRef.current); };
  }, [bgMode, localSlides]);

  // ── Firestore slideshow sync for this entity ──────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'meetingSlides', entityId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (Array.isArray(data.slides)) setLocalSlides(data.slides);
    });
    return () => unsub();
  }, [entityId]);

  const handleSlideUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingSlides(true);
    try {
      const newSlides = [...localSlides];
      for (const file of Array.from(files)) {
        const storageRef = ref(storage, `meetingSlides/${entityId}/${Date.now()}-${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        const type = file.type.startsWith('video/') ? 'video' as const : 'image' as const;
        newSlides.push({ url, name: file.name, type });
      }
      setLocalSlides(newSlides);
      await setDoc(doc(db, 'meetingSlides', entityId), { slides: newSlides, currentIndex: 0, active: false }, { merge: true });
    } catch (err) {
      console.error('Slide upload failed:', err);
    } finally {
      setUploadingSlides(false);
      if (slideInputRef.current) slideInputRef.current.value = '';
    }
  }, [entityId, localSlides]);

  const removeSlide = useCallback(async (idx: number) => {
    const updated = localSlides.filter((_, i) => i !== idx);
    setLocalSlides(updated);
    await setDoc(doc(db, 'meetingSlides', entityId), { slides: updated, currentIndex: 0 }, { merge: true }).catch(() => {});
  }, [entityId, localSlides]);

  const reorderSlide = useCallback(async (from: number, to: number) => {
    if (to < 0 || to >= localSlides.length) return;
    const arr = [...localSlides];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    setLocalSlides(arr);
    await setDoc(doc(db, 'meetingSlides', entityId), { slides: arr }, { merge: true }).catch(() => {});
  }, [entityId, localSlides]);

  // Template CRUD
  useEffect(() => {
    const q2 = query(collection(db, 'slideTemplates'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q2, (snap) => {
      setSlideTemplates(snap.docs
        .map((d) => ({ id: d.id, ...d.data() } as SlideTemplate))
        .filter((t) => t.slides?.length > 0));
    });
    return () => unsub();
  }, []);

  const saveAsTemplate = useCallback(async () => {
    if (!templateName.trim() || localSlides.length === 0) return;
    setSavingTemplate(true);
    try {
      await addDoc(collection(db, 'slideTemplates'), {
        name: templateName.trim(),
        slides: localSlides,
        entityId,
        createdAt: serverTimestamp(),
      });
      setTemplateName('');
    } finally { setSavingTemplate(false); }
  }, [templateName, localSlides, entityId]);

  const loadTemplate = useCallback(async (t: SlideTemplate) => {
    setLocalSlides(t.slides);
    await setDoc(doc(db, 'meetingSlides', entityId), { slides: t.slides, currentIndex: 0, active: false }, { merge: true });
  }, [entityId]);

  const deleteTemplate = useCallback(async (id: string) => {
    await deleteDoc(doc(db, 'slideTemplates', id));
  }, []);

  const saveBgMode = useCallback(async (mode: BgMode) => {
    setBgMode(mode);
    await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), { countdownBgMode: mode, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
  }, [entityId]);

  const saveYtAudioOnly = useCallback(async (v: boolean) => {
    setYtAudioOnly(v);
    await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), { ytAudioOnly: v, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
  }, [entityId]);

  const overlayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveOverlay = useCallback((patch: Record<string, unknown>) => {
    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    overlayTimerRef.current = setTimeout(async () => {
      await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), { ...patch, updatedAt: serverTimestamp() }, { merge: true }).catch(() => {});
    }, 400);
  }, [entityId]);

  // Determine active URLs
  const activeMusicUrl = localMusicUrl.trim() || initialMusicUrl || '';
  const activeImageUrl = localImageUrl.trim() || initialImageUrl || '';
  const ytType = detectYouTubeType(activeMusicUrl);
  const ytEmbedUrl = buildYouTubeEmbedUrl(activeMusicUrl);

  const ytThumbnailUrl = (() => {
    const vid = extractYouTubeId(activeMusicUrl);
    return vid ? `https://img.youtube.com/vi/${vid}/hqdefault.jpg` : null;
  })();

  // ── Firestore sync: listen for countdown state from host ──────────────
  useEffect(() => {
    if (!entityId) return;
    const unsub = onSnapshot(doc(db, COLLECTIONS.SETTINGS, entityId), (snap) => {
      if (!snap.exists()) return;
      const data: any = snap.data();

      const running = !!data?.countdownRunning;
      const startedAtMs: number = data?.countdownStartedAtMs || 0;
      const targetMs: number = data?.meetingTargetMs || 0;
      const durationSec: number = data?.countdownDurationSeconds || timerMinutes * 60;

      if (running && startedAtMs > 0) {
        const remaining = targetMs > 0
          ? Math.max(0, Math.floor((targetMs - Date.now()) / 1000))
          : Math.max(0, durationSec - Math.floor((Date.now() - startedAtMs) / 1000));

        if (remaining <= 0) {
          setState('finished');
          setRemainingSeconds(0);
        } else {
          setState('running');
          setRemainingSeconds(remaining);
          if (!intervalRef.current) {
            intervalRef.current = setInterval(() => {
              if (targetMs > 0) {
                const r = Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
                setRemainingSeconds(r);
                if (r <= 0) {
                  if (intervalRef.current) clearInterval(intervalRef.current);
                  intervalRef.current = null;
                  setState('finished');
                }
              } else {
                setRemainingSeconds((prev) => {
                  if (prev <= 1) {
                    if (intervalRef.current) clearInterval(intervalRef.current);
                    intervalRef.current = null;
                    setState('finished');
                    return 0;
                  }
                  return prev - 1;
                });
              }
            }, 1_000);
          }
        }
      } else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (state === 'running') {
          setState('idle');
          setRemainingSeconds(durationSec);
        }
      }

      // Sync host settings to all attendees
      if (data?.countdownMusicUrl && !localMusicUrl) {
        setLocalMusicUrl(data.countdownMusicUrl);
      }
      if (data?.countdownImageUrl && !localImageUrl) {
        setLocalImageUrl(data.countdownImageUrl);
      }
      if (data?.countdownBgMode) setBgMode(data.countdownBgMode);
      // Altijd audio-only — Full video optie is verwijderd
      setYtAudioOnly(true);
      if (data?.overlayColor1) setOverlayColor1(data.overlayColor1);
      if (data?.overlayColor2) setOverlayColor2(data.overlayColor2);
      if (typeof data?.overlayOpacity === 'number') setOverlayOpacity(data.overlayOpacity);
      if (typeof data?.overlayAngle === 'number') setOverlayAngle(data.overlayAngle);
      if (typeof data?.countdownMinutes === 'number' && data.countdownMinutes > 0) {
        setTimerMinutes(data.countdownMinutes);
        if (state === 'idle') setRemainingSeconds(data.countdownMinutes * 60);
      }
      if (typeof data?.meetingTimeHour === 'number') setMeetingTimeHour(data.meetingTimeHour);
      if (typeof data?.meetingTimeMinute === 'number') setMeetingTimeMinute(data.meetingTimeMinute);
      if (data?.hostTimezone) setHostTimezone(data.hostTimezone);
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, countdownMinutes]);

  // (Geen auto-join meer — leden kiezen zelf via knoppen)

  // ── Start music/video when state transitions to running ───────────────
  useEffect(() => {
    if (state === 'running' && !musicStarted) {
      setMusicStarted(true);
      setMusicError(null);

      if (ytEmbedUrl) {
        try {
          ytPlayerRef.current?.contentWindow?.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            '*',
          );
        } catch { /* autoplay param handles it */ }
      } else if (audioRef.current) {
        audioRef.current.play().catch(() => {
          setMusicError('Muziek kon niet starten. Klik ergens op de pagina en probeer opnieuw.');
        });
      }

      videoRef.current?.play().catch(() => {
        setVideoError('Video kon niet worden afgespeeld.');
      });
    }
    if (state !== 'running') {
      setMusicStarted(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ── Audio init (non-YouTube) ────────────────────────────────────────────
  useEffect(() => {
    if (!activeMusicUrl || ytType) return;
    setIsMusicLoading(true);
    const audio = new Audio(activeMusicUrl);
    audio.loop = true;
    audio.volume = 0.45;
    audio.oncanplaythrough = () => setIsMusicLoading(false);
    audio.onerror = () => {
      setMusicError(STRINGS.errors.generic + ' (muziek)');
      setIsMusicLoading(false);
    };
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [activeMusicUrl, ytType]);

  // ── Cleanup on unmount ────────────────────────────────────────────────
  useEffect(() => {
    return () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
      audioRef.current?.pause();
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      audioCtxRef.current?.close().catch(() => {});
    };
  }, []);

  // ── Spectrum analyser (reactive to real audio / ambient for YouTube) ────
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const sourceConnectedRef = useRef(false);
  const prevValuesRef = useRef<number[]>(new Array(128).fill(0));

  const ensureAudioCtx = useCallback(() => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const AC = (window.AudioContext ?? (window as any).webkitAudioContext) as typeof AudioContext;
    const ctx = new AC();
    audioCtxRef.current = ctx;
    return ctx;
  }, []);

  /** Connect the HTML <audio> element to AnalyserNode (direct URL). */
  const connectAnalyserDirect = useCallback(() => {
    if (!audioRef.current || sourceConnectedRef.current) return;
    try {
      const ctx = ensureAudioCtx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.78;
      const src = ctx.createMediaElementSource(audioRef.current);
      src.connect(analyser);
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;
      sourceConnectedRef.current = true;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    } catch (e) {
      console.warn('[Spectrum] Direct audio connect failed:', e);
    }
  }, [ensureAudioCtx]);

  /** Render one frame of the spectrum visualiser. */
  const drawFrame = useCallback((isRunning: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx2d = canvas.getContext('2d');
    if (!ctx2d) return;

    const cssW = canvas.offsetWidth;
    const cssH = canvas.offsetHeight;
    if (canvas.width !== cssW || canvas.height !== cssH) {
      canvas.width = cssW;
      canvas.height = cssH;
    }
    const W = canvas.width;
    const H = canvas.height;
    const BAR_COUNT = 128;

    // ── Gather frequency data ──
    let rawValues: number[];
    const hasRealData = !!analyserRef.current;

    if (hasRealData) {
      const bufLen = analyserRef.current!.frequencyBinCount;
      const buf = new Uint8Array(bufLen);
      analyserRef.current!.getByteFrequencyData(buf);
      rawValues = Array.from({ length: BAR_COUNT }, (_, i) => {
        const idx = Math.floor((i / BAR_COUNT) * bufLen * 0.75);
        return buf[idx] / 255;
      });
    } else if (isRunning) {
      // YouTube or no analyser — organic ambient wave tied to time
      const t = performance.now() / 1000;
      rawValues = Array.from({ length: BAR_COUNT }, (_, i) => {
        const pos = i / BAR_COUNT;
        const wave1 = Math.sin(t * 1.2 + pos * Math.PI * 4) * 0.18;
        const wave2 = Math.sin(t * 0.7 + pos * Math.PI * 7) * 0.12;
        const wave3 = Math.sin(t * 2.1 + pos * Math.PI * 2.3) * 0.08;
        const center = 1 - Math.abs(pos - 0.5) * 1.4;
        return Math.max(0.03, (0.15 + wave1 + wave2 + wave3) * Math.max(0.3, center));
      });
    } else {
      rawValues = new Array(BAR_COUNT).fill(0.01);
    }

    // Smooth over time for organic movement
    const prev = prevValuesRef.current;
    const smoothing = hasRealData ? 0.35 : 0.12;
    const values = rawValues.map((v, i) => {
      const s = prev[i] !== undefined ? prev[i] * smoothing + v * (1 - smoothing) : v;
      return s;
    });
    prevValuesRef.current = values;

    // ── Draw bars ──
    ctx2d.clearRect(0, 0, W, H);
    const barW = W / BAR_COUNT;
    const gap = Math.max(0.5, barW * 0.12);

    for (let i = 0; i < BAR_COUNT; i++) {
      const val = values[i];
      const barH = Math.max(1, val * H * 0.95);
      const x = i * barW;

      const hue = (i / BAR_COUNT) * 280 + 110;
      const brightness = 48 + val * 25;
      const alpha = isRunning ? 0.5 + val * 0.5 : 0.1 + val * 0.15;

      const grad = ctx2d.createLinearGradient(x, H - barH, x, H);
      grad.addColorStop(0, `hsla(${hue}, 95%, ${brightness}%, ${alpha})`);
      grad.addColorStop(0.5, `hsla(${hue + 12}, 90%, 42%, ${alpha * 0.7})`);
      grad.addColorStop(1, `hsla(${hue + 25}, 85%, 30%, 0)`);

      ctx2d.fillStyle = grad;
      const bw = Math.max(1, barW - gap);
      ctx2d.beginPath();
      const radius = Math.min(bw / 2, 2);
      const bx = x + gap / 2;
      const by = H - barH;
      ctx2d.moveTo(bx + radius, by);
      ctx2d.lineTo(bx + bw - radius, by);
      ctx2d.quadraticCurveTo(bx + bw, by, bx + bw, by + radius);
      ctx2d.lineTo(bx + bw, H);
      ctx2d.lineTo(bx, H);
      ctx2d.lineTo(bx, by + radius);
      ctx2d.quadraticCurveTo(bx, by, bx + radius, by);
      ctx2d.fill();
    }

    animFrameRef.current = requestAnimationFrame(() => drawFrame(isRunning));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Start / restart the visualiser when state changes. */
  useEffect(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    const running = state === 'running';

    if (running && !ytEmbedUrl && audioRef.current) {
      connectAnalyserDirect();
    }

    drawFrame(running);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ── Image upload (host only) ──────────────────────────────────────────
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    setUploadingImage(true);
    try {
      const path = `groupHeaders/${entityId}/countdown_${Date.now()}.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setLocalImageUrl(url);
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        { countdownImageUrl: url, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      console.error('[MeetingCountdown] Image upload error:', err);
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Save music URL (host only) ────────────────────────────────────────
  const saveMusicUrl = async () => {
    setSavingMusic(true);
    setMusicSaved(false);
    try {
      // ytAudioOnly altijd true: muziek is puur achtergrondaudio, geen full video
      setYtAudioOnly(true);
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        { countdownMusicUrl: localMusicUrl.trim() || null, ytAudioOnly: true, updatedAt: serverTimestamp() },
        { merge: true },
      );
      setMusicSaved(true);
      setTimeout(() => setMusicSaved(false), 2500);
    } catch (err) {
      console.error('[MeetingCountdown] Save music URL error:', err);
      setMusicError('Opslaan mislukt. Probeer opnieuw.');
    } finally {
      setSavingMusic(false);
    }
  };

  // ── Host: start countdown (writes to Firestore → all clients react) ───
  const handleStart = async (): Promise<void> => {
    if (state !== 'idle') return;
    const durationSec = autoCountdownSec > 0 ? autoCountdownSec : timerMinutes * 60;
    try {
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        {
          countdownRunning: true,
          countdownStartedAtMs: Date.now(),
          countdownDurationSeconds: durationSec,
          meetingTargetMs: meetingTargetMs,
          meetingTimeHour,
          meetingTimeMinute,
          hostTimezone,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (err) {
      console.error('[MeetingCountdown] Failed to start countdown:', err);
    }
  };

  // ── Host: stop countdown ──────────────────────────────────────────────
  const handleStop = async (): Promise<void> => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    audioRef.current?.pause();
    try {
      ytPlayerRef.current?.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*',
      );
    } catch { /* ignore */ }

    setState('idle');
    setRemainingSeconds(autoCountdownSec > 0 ? autoCountdownSec : timerMinutes * 60);

    try {
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        { countdownRunning: false, updatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (err) {
      console.error('[MeetingCountdown] Failed to stop countdown:', err);
    }
  };

  // ── Host: meeting starten ─────────────────────────────────────────────
  const handleMeetingStart = async (): Promise<void> => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    audioRef.current?.pause();
    try {
      ytPlayerRef.current?.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*',
      );
    } catch { /* ignore */ }

    try {
      await setDoc(
        doc(db, COLLECTIONS.SETTINGS, entityId),
        {
          countdownRunning: false,
          meetingStartedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } catch (err) {
      console.error('[MeetingCountdown] Failed to write meeting start:', err);
    }
    onMeetingStart();
  };

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  const label = STRINGS.countdown.heading(minutes > 0 ? minutes : 1);

  return (
    <div
      className="fixed inset-0 z-[85] flex flex-col items-center justify-center text-white"
      style={{ background: '#0a0a0a' }}
      role="dialog"
      aria-modal="true"
      aria-label="Meeting countdown"
    >
      {/* ── Background layer — driven by bgMode ─────────────────── */}

      {/* MODE: image — single background image */}
      {bgMode === 'image' && activeImageUrl && (
        <img src={activeImageUrl} alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none" />
      )}

      {/* MODE: slideshow — cycle through uploaded slides */}
      {bgMode === 'slideshow' && localSlides.length > 0 && (() => {
        const slide = localSlides[bgSlideIdx % localSlides.length];
        return slide?.type === 'video' ? (
          <video key={`bg-${bgSlideIdx}`} src={slide.url} muted loop autoPlay playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none transition-opacity duration-1000" />
        ) : (
          <img key={`bg-${bgSlideIdx}`} src={slide?.url} alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30 pointer-events-none transition-opacity duration-1000" />
        );
      })()}

      {/* MODE: youtube — only play when countdown is running */}
      {bgMode === 'youtube' && ytEmbedUrl && state === 'running' && (
        !ytAudioOnly ? (
          <iframe ref={ytPlayerRef} src={ytEmbedUrl} allow="autoplay"
            className="absolute inset-0 w-full h-full pointer-events-none opacity-30"
            style={{ border: 'none', transform: 'scale(1.3)', transformOrigin: 'center center' }}
            title="YouTube video achtergrond" />
        ) : (
          <iframe ref={ytPlayerRef} src={ytEmbedUrl} allow="autoplay"
            className="absolute w-0 h-0 opacity-0 pointer-events-none"
            title="Countdown muziek" />
        )
      )}

      {/* MODE: youtube idle preview — show thumbnail before countdown starts */}
      {bgMode === 'youtube' && !ytAudioOnly && ytThumbnailUrl && state !== 'running' && (
        <img src={ytThumbnailUrl} alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none" />
      )}

      {/* Non-youtube bgMode still needs YouTube audio if URL is set but bgMode != youtube */}
      {bgMode !== 'youtube' && ytEmbedUrl && state === 'running' && (
        <iframe ref={ytPlayerRef} src={ytEmbedUrl} allow="autoplay"
          className="absolute w-0 h-0 opacity-0 pointer-events-none"
          title="Countdown muziek" />
      )}

      {/* Legacy video prop fallback */}
      {bgMode === 'image' && videoUrl && !videoError && (
        <video ref={videoRef} src={videoUrl} muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none"
          onError={() => setVideoError('Video kon niet worden geladen.')} />
      )}

      {/* ── Color overlay (customisable gradient on top of media) ─── */}
      <div className="absolute inset-0 pointer-events-none z-[1]" style={{
        background: `linear-gradient(${overlayAngle}deg, ${overlayColor1} 0%, ${overlayColor2} 100%)`,
        opacity: overlayOpacity,
      }} />

      {/* ── World Clock Bar (top) — host clock = rode draad ────── */}
      <div className="absolute top-0 left-0 right-0 z-10 pointer-events-auto" style={{ transformOrigin: 'top center', transform: `scale(${clockZoom})` }}>
        <div className="flex items-end justify-center gap-0 px-2 pt-2 pb-1 overflow-x-auto">
          {/* Left world clocks */}
          {WORLD_ZONES.filter(z => z.tz !== hostTimezone).slice(0, 4).map(({ tz, label }) => {
            const isUser = tz === userTimezone;
            return (
              <div key={tz} className={`flex flex-col items-center px-2 py-1 min-w-[56px] rounded-lg transition-all ${
                isUser ? 'bg-white/[0.08] border border-white/[0.08]' : ''
              }`}>
                <MiniAnalogClock tz={tz} nowTick={nowTick} size={24} opacity={isUser ? 0.7 : 0.25} />
                <p className={`text-[8px] font-medium leading-tight mt-0.5 ${isUser ? 'text-white/70' : 'text-white/25'}`}>{label}</p>
                <p className={`text-[9px] font-mono tabular-nums leading-tight ${isUser ? 'text-white/60' : 'text-white/18'}`}>{formatTimeInZone(tz)}</p>
                {isUser && <span className="text-[6px] text-sky-400/70 font-bold uppercase tracking-wider">Jij</span>}
              </div>
            );
          })}

          {/* CENTER: Host master clock — always visible, larger */}
          {(() => {
            const hostZone = WORLD_ZONES.find(z => z.tz === hostTimezone) || { tz: hostTimezone, label: hostTimezone.split('/').pop()?.replace(/_/g, ' ') || 'Host' };
            return (
              <div className="flex flex-col items-center px-4 py-2 mx-1 min-w-[90px] rounded-2xl bg-white/[0.12] border border-white/[0.15] backdrop-blur-sm">
                <MiniAnalogClock tz={hostZone.tz} nowTick={nowTick} size={36} opacity={1} accent="#f59e0b" />
                <p className="text-[10px] font-semibold text-white leading-tight mt-1">{hostZone.label}</p>
                <p className="text-sm font-black text-white tabular-nums tracking-tight">{formatTimeInZone(hostZone.tz)}</p>
                <span className="text-[7px] text-amber-400 font-bold uppercase tracking-widest">Host</span>
              </div>
            );
          })()}

          {/* Right world clocks */}
          {WORLD_ZONES.filter(z => z.tz !== hostTimezone).slice(4).map(({ tz, label }) => {
            const isUser = tz === userTimezone;
            return (
              <div key={tz} className={`flex flex-col items-center px-2 py-1 min-w-[56px] rounded-lg transition-all ${
                isUser ? 'bg-white/[0.08] border border-white/[0.08]' : ''
              }`}>
                <MiniAnalogClock tz={tz} nowTick={nowTick} size={24} opacity={isUser ? 0.7 : 0.25} />
                <p className={`text-[8px] font-medium leading-tight mt-0.5 ${isUser ? 'text-white/70' : 'text-white/25'}`}>{label}</p>
                <p className={`text-[9px] font-mono tabular-nums leading-tight ${isUser ? 'text-white/60' : 'text-white/18'}`}>{formatTimeInZone(tz)}</p>
                {isUser && <span className="text-[6px] text-sky-400/70 font-bold uppercase tracking-wider">Jij</span>}
              </div>
            );
          })}
        </div>

        {/* Zoom toggle — +/- knoppen */}
        <div className="absolute top-2 right-3 flex items-center gap-1 pointer-events-auto">
          <button onClick={() => setClockZoom(z => Math.max(0.7, z - 0.15))}
            className="w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 text-white/50 hover:text-white flex items-center justify-center text-[10px] font-bold transition-colors">−</button>
          <button onClick={() => setClockZoom(z => Math.min(1.5, z + 0.15))}
            className="w-5 h-5 rounded-full bg-white/10 hover:bg-white/20 text-white/50 hover:text-white flex items-center justify-center text-[10px] font-bold transition-colors">+</button>
        </div>
      </div>

      {/* Branding logo — below timezone bar, top-right */}
      {logoUrl && (
        <div className="absolute top-[72px] right-5 z-20 pointer-events-none">
          <img
            src={logoUrl}
            alt={clubName || 'Logo'}
            className="h-10 w-auto max-w-[140px] object-contain drop-shadow-lg"
          />
        </div>
      )}

      {/* Sluiten: host stopt lokaal; gast minimaliseert (countdown blijft in Firestore lopen) */}
      {(isHost || state === 'running') && (
      <button
          type="button"
          onClick={() => {
            audioRef.current?.pause();
            try {
              ytPlayerRef.current?.contentWindow?.postMessage(
                '{"event":"command","func":"pauseVideo","args":""}', '*',
              );
            } catch { /* ignore */ }
            onClose();
          }}
        aria-label={isHost ? 'Countdown sluiten' : 'Countdown minimaliseren'}
          title={isHost ? 'Sluiten' : 'Minimaliseren (countdown loopt door)'}
          className={`absolute top-[72px] w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 transition-colors text-sm font-bold z-20 ${logoUrl ? 'right-[calc(1.25rem+150px)]' : 'right-5'}`}
      >
        ✕
      </button>
      )}

      {/* ── Advanced Settings Panel (host only) ────────────────────────── */}
      {isHost && showSettings && state === 'idle' && (
        <div className="absolute inset-4 z-20 flex items-center justify-center gap-0 pointer-events-none">
          <div
            className="pointer-events-auto w-full max-w-[960px] h-[min(90vh,780px)] bg-black/70 backdrop-blur-2xl rounded-3xl border border-white/[0.08] shadow-2xl flex overflow-hidden"
            style={{ animation: 'settingsFadeIn 0.3s ease-out' }}
          >

            {/* ── Left: Navigation tabs ─────────────────────────────── */}
            <div className="w-[200px] shrink-0 border-r border-white/[0.06] flex flex-col py-4 px-3 overflow-y-auto">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/30 px-3 mb-4">Instellingen</p>
              {settingsNavItems.map(({ key, label, Icon }) => (
                <button key={key} onClick={() => setSettingsTab(key)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all mb-0.5 ${
                    settingsTab === key
                      ? 'bg-white/[0.12] text-white'
                      : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
                  }`}>
                  <Icon size={15} weight="regular" className="shrink-0 opacity-70" />
                  <span className="font-medium text-[13px] min-w-0">{label}</span>
                  {key === 'slides' && localSlides.length > 0 && (
                    <span className="ml-auto text-[9px] bg-white/10 text-white/50 px-1.5 py-0.5 rounded-full">{localSlides.length}</span>
                  )}
                  {key === 'timer' && (
                    <span className="ml-auto text-[9px] bg-white/10 text-white/50 px-1.5 py-0.5 rounded-full tabular-nums">{String(meetingTimeHour).padStart(2, '0')}:{String(meetingTimeMinute).padStart(2, '0')}</span>
                  )}
                </button>
              ))}

              <div className="mt-auto pt-4 border-t border-white/[0.06] mx-3">
                <button onClick={() => setShowSettings(false)}
                  className="w-full h-9 text-[12px] font-semibold text-white/60 hover:text-white bg-white/[0.06] hover:bg-white/[0.12] rounded-xl transition-all">
                  Sluiten
                </button>
              </div>
            </div>

            {/* ── Right: Content area ───────────────────────────────── */}
            <div className="flex-1 min-h-0 overflow-y-auto py-4 px-6">

              {/* ── Commitments tab ──────────────────────────────────── */}
              {settingsTab === 'commitments' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-[14px] font-semibold text-white mb-0.5">Mijn commitments</h3>
                    <p className="text-[10px] text-white/40 leading-relaxed">
                      Stel maximaal 6 actiepunten in voor deze sessie. Eerdere commitments zijn vooraf ingeladen. Pas ze aan of voeg nieuwe toe.
                    </p>
                  </div>

                  {commitmentLoading ? (
                    <div className="flex items-center justify-center py-10">
                      <svg className="w-5 h-5 text-white/30 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {commitmentSlots.map((slot, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          {/* Nummer badge */}
                          <div className={`shrink-0 w-6 h-6 mt-2.5 rounded-full flex items-center justify-center text-[10px] font-bold border transition-colors ${
                            slot.text.trim()
                              ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                              : 'bg-white/5 border-white/10 text-white/25'
                          }`}>
                            {idx + 1}
                          </div>

                          {/* Tekstveld */}
                          <div className="flex-1 relative">
                            <textarea
                              value={slot.text}
                              rows={2}
                              placeholder={`Commitment ${idx + 1}…`}
                              onChange={e => setCommitmentSlots(prev => prev.map((s, i) =>
                                i === idx ? { ...s, text: e.target.value, saved: false } : s
                              ))}
                              onBlur={() => void saveCommitmentSlot(idx, commitmentSlots)}
                              className="w-full bg-white/5 border border-white/10 focus:border-emerald-500/50 rounded-xl px-3 py-2.5 text-[12px] text-white/85 placeholder:text-white/20 resize-none focus:outline-none transition-colors leading-snug"
                            />
                            {/* Status indicator */}
                            <div className="absolute bottom-2 right-2 flex items-center gap-1">
                              {slot.saving && (
                                <svg className="w-3 h-3 text-white/30 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                                </svg>
                              )}
                              {!slot.saving && slot.saved && slot.text.trim() && (
                                <svg className="w-3 h-3 text-emerald-400/60" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {!slot.saving && !slot.saved && slot.text.trim() && (
                                <span className="text-[8px] text-white/20">●</span>
                              )}
                            </div>
                          </div>

                          {/* Verwijder knop — alleen zichtbaar als er tekst is */}
                          {slot.text.trim() ? (
                            <button
                              type="button"
                              onClick={() => void deleteCommitmentSlot(idx)}
                              title="Verwijder commitment"
                              className="shrink-0 w-6 h-6 mt-2.5 rounded-full flex items-center justify-center text-white/20 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          ) : (
                            <div className="shrink-0 w-6 mt-2.5" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="text-[9px] text-white/20 text-center">
                    Automatisch opgeslagen bij het verlaten van een veld. Commitments zijn zichtbaar tijdens de meeting.
                  </p>
                </div>
              )}

              {settingsTab === 'achtergrond' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-[14px] font-semibold text-white mb-0.5">Achtergrond</h3>
                  <p className="text-[10px] text-white/40">Kies hoe de countdown er uitziet voor deelnemers.</p>
                </div>

                  {/* Mode toggle */}
            <div className="flex gap-2">
                    {([
                      { mode: 'image' as BgMode, label: 'Enkele afbeelding', desc: 'Eén vaste foto' },
                      { mode: 'slideshow' as BgMode, label: 'Slideshow', desc: 'Wisselende slides' },
                      { mode: 'youtube' as BgMode, label: 'YouTube video', desc: 'Video als achtergrond' },
                    ]).map(({ mode, label, desc }) => (
                      <button key={mode} onClick={() => saveBgMode(mode)}
                        className={`flex-1 p-4 rounded-2xl border-2 text-left transition-all ${
                          bgMode === mode
                            ? 'border-white/30 bg-white/[0.08]'
                            : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.12] hover:bg-white/[0.04]'
                        }`}>
                        <p className={`text-[13px] font-semibold ${bgMode === mode ? 'text-white' : 'text-white/50'}`}>{label}</p>
                        <p className="text-[10px] text-white/30 mt-0.5">{desc}</p>
              </button>
                    ))}
                  </div>

                  {/* Single image mode */}
                  {bgMode === 'image' && (
                    <div className="space-y-3">
                      {/* Preview */}
                      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06]">
                        {activeImageUrl ? (
                          <img src={activeImageUrl} alt="Preview" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <span className="text-3xl opacity-20 mb-2">◻</span>
                            <p className="text-[11px] text-white/20">Geen afbeelding ingesteld</p>
                          </div>
                        )}
              {activeImageUrl && (
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => imageInputRef.current?.click()} disabled={uploadingImage}
                          className="flex-1 h-10 text-[12px] font-semibold bg-white/[0.08] hover:bg-white/[0.14] rounded-xl border border-white/[0.08] transition-all disabled:opacity-40">
                          {uploadingImage ? 'Uploaden...' : activeImageUrl ? 'Andere afbeelding' : 'Afbeelding kiezen'}
                        </button>
                        {activeImageUrl && (
                          <button onClick={async () => {
                    setLocalImageUrl('');
                            await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), { countdownImageUrl: null, updatedAt: serverTimestamp() }, { merge: true });
                          }} className="h-10 px-4 text-[12px] font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-xl border border-red-500/10 transition-colors">
                            Verwijderen
                </button>
              )}
            </div>
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    </div>
                  )}

                  {/* Slideshow mode — quick preview */}
                  {bgMode === 'slideshow' && (
                    <div className="space-y-3">
                      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06]">
                        {localSlides.length > 0 ? (
                          <>
                            {localSlides[previewSlideIdx]?.type === 'video' ? (
                              <video src={localSlides[previewSlideIdx].url} className="w-full h-full object-cover" muted autoPlay loop />
                            ) : (
                              <img src={localSlides[previewSlideIdx]?.url} alt="" className="w-full h-full object-cover" />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                              <p className="text-[11px] text-white/60 font-medium">{localSlides[previewSlideIdx]?.name}</p>
                              <span className="text-[10px] text-white/40 tabular-nums">{previewSlideIdx + 1} / {localSlides.length}</span>
          </div>
                            {localSlides.length > 1 && (
                              <>
                                <button onClick={() => setPreviewSlideIdx((i) => Math.max(0, i - 1))} disabled={previewSlideIdx === 0}
                                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 disabled:opacity-20 flex items-center justify-center transition-all">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                                </button>
                                <button onClick={() => setPreviewSlideIdx((i) => Math.min(localSlides.length - 1, i + 1))} disabled={previewSlideIdx >= localSlides.length - 1}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 disabled:opacity-20 flex items-center justify-center transition-all">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                </button>
                              </>
                            )}
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <span className="text-3xl opacity-20 mb-2">▶</span>
                            <p className="text-[11px] text-white/20">Nog geen slides. Ga naar het Slideshow tabblad</p>
                          </div>
                        )}
                      </div>
                      <button onClick={() => setSettingsTab('slides')}
                        className="w-full h-10 text-[12px] font-semibold bg-white/[0.08] hover:bg-white/[0.14] rounded-xl border border-white/[0.08] transition-all">
                        Slides beheren →
                      </button>
                    </div>
                  )}

                  {/* YouTube video mode */}
                  {bgMode === 'youtube' && (
                    <div className="space-y-3">
                      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06]">
                        {ytThumbnailUrl ? (
                          <>
                            <img src={ytThumbnailUrl} alt="YouTube" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                              <div className="w-14 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                              <p className="text-[10px] text-white/50 truncate">{activeMusicUrl}</p>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center">
                            <span className="text-3xl opacity-20 mb-2">♫</span>
                            <p className="text-[11px] text-white/20">Stel een YouTube URL in bij het Muziek tabblad</p>
                          </div>
                        )}
                      </div>

                      {/* Muziek speelt altijd audio-only */}
                      <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                        <MusicNote size={13} className="text-white/35 shrink-0" />
                        <p className="text-[11px] text-white/40">Muziek speelt als achtergrondaudio. Stel de URL in via het Muziek-tabblad.</p>
                      </div>

                      {!activeMusicUrl && (
                        <button onClick={() => setSettingsTab('muziek')}
                          className="w-full h-10 text-[12px] font-semibold bg-white/[0.08] hover:bg-white/[0.14] rounded-xl border border-white/[0.08] transition-all">
                          YouTube URL instellen →
                        </button>
                      )}
                    </div>
                  )}

                  {/* ── Overlay Filter (collapsible) ─────────────── */}
                  <details className="pt-3 border-t border-white/[0.06] group">
                    <summary className="flex items-center justify-between cursor-pointer list-none select-none">
                      <div>
                        <h4 className="text-[12px] font-semibold text-white mb-0">Overlay filter</h4>
                        <p className="text-[9px] text-white/35">Kleur-overlay over de achtergrond</p>
                      </div>
                      <span className="text-[10px] text-white/30 group-open:rotate-90 transition-transform">▶</span>
                    </summary>
                    <div className="space-y-3 pt-3">

                    {/* Live preview */}
                    <div className="relative w-full h-16 rounded-xl overflow-hidden border border-white/[0.08]">
                      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMzMzIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMzMzMiLz48cmVjdCB4PSIxMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMjIyIi8+PHJlY3QgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iIzIyMiIvPjwvc3ZnPg==')] opacity-40" />
                      <div className="absolute inset-0" style={{
                        background: `linear-gradient(${overlayAngle}deg, ${overlayColor1} 0%, ${overlayColor2} 100%)`,
                        opacity: overlayOpacity,
                      }} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[11px] font-bold text-white/80 drop-shadow-lg tracking-wide">PREVIEW</span>
                      </div>
                    </div>

                    {/* Color pickers */}
                    <div className="flex gap-3">
                      <label className="flex-1 space-y-1">
                        <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Kleur 1</span>
                        <div className="flex items-center gap-2">
                          <input type="color" value={overlayColor1}
                            onChange={(e) => { setOverlayColor1(e.target.value); saveOverlay({ overlayColor1: e.target.value }); }}
                            className="w-9 h-9 rounded-lg border border-white/[0.1] bg-transparent cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none" />
                          <input type="text" value={overlayColor1}
                            onChange={(e) => { setOverlayColor1(e.target.value); saveOverlay({ overlayColor1: e.target.value }); }}
                            className="flex-1 h-9 px-3 text-[11px] font-mono text-white/70 bg-white/[0.04] border border-white/[0.08] rounded-lg focus:border-white/20 focus:outline-none transition-colors" />
                        </div>
                      </label>
                      <label className="flex-1 space-y-1">
                        <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Kleur 2</span>
                        <div className="flex items-center gap-2">
                          <input type="color" value={overlayColor2}
                            onChange={(e) => { setOverlayColor2(e.target.value); saveOverlay({ overlayColor2: e.target.value }); }}
                            className="w-9 h-9 rounded-lg border border-white/[0.1] bg-transparent cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch]:border-none" />
                          <input type="text" value={overlayColor2}
                            onChange={(e) => { setOverlayColor2(e.target.value); saveOverlay({ overlayColor2: e.target.value }); }}
                            className="flex-1 h-9 px-3 text-[11px] font-mono text-white/70 bg-white/[0.04] border border-white/[0.08] rounded-lg focus:border-white/20 focus:outline-none transition-colors" />
                        </div>
                      </label>
                    </div>

                    {/* Preset palette */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Snelkeuze</span>
                      <div className="flex gap-1.5 flex-wrap">
                        {[
                          { c1: '#166534', c2: '#0f172a', label: 'Bos' },
                          { c1: '#1e3a5f', c2: '#0f172a', label: 'Ocean' },
                          { c1: '#7c2d12', c2: '#1c1917', label: 'Warm' },
                          { c1: '#581c87', c2: '#0f172a', label: 'Royal' },
                          { c1: '#0f172a', c2: '#0f172a', label: 'Donker' },
                          { c1: '#b45309', c2: '#422006', label: 'Goud' },
                          { c1: '#be123c', c2: '#1c1917', label: 'Rose' },
                          { c1: '#0e7490', c2: '#164e63', label: 'Teal' },
                        ].map(({ c1, c2, label }) => (
                          <button key={label} onClick={() => {
                            setOverlayColor1(c1); setOverlayColor2(c2);
                            saveOverlay({ overlayColor1: c1, overlayColor2: c2 });
                          }}
                            className={`h-7 px-2.5 rounded-lg border text-[10px] font-medium transition-all ${
                              overlayColor1 === c1 && overlayColor2 === c2
                                ? 'border-white/30 text-white bg-white/[0.1]'
                                : 'border-white/[0.06] text-white/40 hover:border-white/[0.15] hover:text-white/60'
                            }`}
                            style={{ backgroundImage: `linear-gradient(135deg, ${c1}80 0%, ${c2}80 100%)` }}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Transparency slider */}
                    <label className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Transparantie</span>
                        <span className="text-[11px] font-mono text-white/50 tabular-nums">{Math.round(overlayOpacity * 100)}%</span>
                      </div>
                      <input type="range" min={0} max={100} value={Math.round(overlayOpacity * 100)}
                        onChange={(e) => { const v = Number(e.target.value) / 100; setOverlayOpacity(v); saveOverlay({ overlayOpacity: v }); }}
                        className="w-full h-1.5 rounded-full appearance-none bg-white/[0.08] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer" />
                    </label>

                    {/* Angle slider */}
                    <label className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Verloop richting</span>
                        <span className="text-[11px] font-mono text-white/50 tabular-nums">{overlayAngle}°</span>
                      </div>
                      <input type="range" min={0} max={360} value={overlayAngle}
                        onChange={(e) => { const v = Number(e.target.value); setOverlayAngle(v); saveOverlay({ overlayAngle: v }); }}
                        className="w-full h-1.5 rounded-full appearance-none bg-white/[0.08] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-pointer" />
                    </label>
                    </div>
                  </details>
                </div>
              )}

              {settingsTab === 'muziek' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-[15px] font-semibold text-white mb-1">Muziek</h3>
                    <p className="text-[11px] text-white/40">Achtergrondmuziek die speelt zodra de countdown start.</p>
                  </div>

                  <div className="space-y-3">
                    <label className="text-[11px] font-semibold text-white/60">YouTube of directe audio URL</label>
                    <div className="flex gap-2">
                      <input type="text" value={localMusicUrl}
                        onChange={(e) => { setLocalMusicUrl(e.target.value); setMusicSaved(false); }}
                        placeholder="https://youtube.com/watch?v=... of playlist"
                        className="flex-1 h-10 px-4 text-[12px] bg-white/[0.06] border border-white/[0.08] rounded-xl text-white placeholder:text-white/20 outline-none focus:border-white/20 transition-colors" />
                      <button onClick={saveMusicUrl} disabled={savingMusic}
                        className={`h-10 px-5 text-[12px] font-semibold rounded-xl border transition-all ${
                          musicSaved
                            ? 'bg-emerald-500/20 border-emerald-400/20 text-emerald-400'
                            : 'bg-white/[0.08] hover:bg-white/[0.14] border-white/[0.08]'
                        }`}>
                        {savingMusic ? '...' : musicSaved ? '✓ Opgeslagen' : 'Opslaan'}
                      </button>
                    </div>

                    {/* YouTube thumbnail + detection */}
                    {ytThumbnailUrl && (
                      <div className="flex gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                        <div className="w-24 h-16 rounded-lg overflow-hidden shrink-0 bg-black">
                          <img src={ytThumbnailUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-medium text-white/70">
                            {ytType === 'playlist' ? 'YouTube afspeellijst' : 'YouTube video'}
                          </p>
                          <p className="text-[10px] text-white/30 mt-0.5 truncate">{activeMusicUrl}</p>
                          <p className="text-[9px] text-white/20 mt-1">
                            {ytType === 'playlist' ? 'Nummers spelen achter elkaar af' : 'Audio speelt bij start countdown'}
                          </p>
                        </div>
                      </div>
                    )}
                    {activeMusicUrl && !ytType && activeMusicUrl.startsWith('http') && (
                      <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                        <span className="text-lg">🔗</span>
                        <p className="text-[11px] text-white/50">Directe audio URL · speelt bij start countdown</p>
                      </div>
                    )}

                    {/* Altijd audio-only: geen video als achtergrond vanuit muziek-tab */}
                    {ytType && (
                      <div className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                        <MusicNote size={14} className="text-white/40 shrink-0" />
                        <p className="text-[11px] text-white/50 leading-snug">
                          Muziek speelt op de achtergrond tijdens de countdown.<br />
                          <span className="text-white/30">Stopt automatisch wanneer de meeting start of de overlay gesloten wordt.</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {settingsTab === 'slides' && (
              <div className="space-y-6">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-[15px] font-semibold text-white mb-1">Slideshow</h3>
                      <p className="text-[11px] text-white/40">Upload afbeeldingen en video clips voor presentaties.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <input ref={slideInputRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple className="hidden" onChange={handleSlideUpload} />
                      <button onClick={() => slideInputRef.current?.click()} disabled={uploadingSlides}
                        className="h-9 px-4 text-[11px] font-semibold bg-white/[0.10] hover:bg-white/[0.16] rounded-xl border border-white/[0.08] transition-all disabled:opacity-40">
                        {uploadingSlides ? 'Uploaden...' : '+ Bestanden toevoegen'}
                      </button>
                    </div>
                  </div>

                  {/* Slide grid with large preview */}
                  {localSlides.length > 0 ? (
                    <div className="flex gap-4">
                      {/* Large preview */}
                      <div className="flex-1 space-y-3">
                        <div className="relative aspect-video rounded-2xl overflow-hidden bg-white/[0.03] border border-white/[0.06]">
                          {localSlides[previewSlideIdx]?.type === 'video' ? (
                            <video src={localSlides[previewSlideIdx].url} className="w-full h-full object-cover" muted autoPlay loop controls />
                          ) : (
                            <img src={localSlides[previewSlideIdx]?.url} alt="" className="w-full h-full object-cover" />
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                            <p className="text-[11px] text-white/70 font-medium truncate">{localSlides[previewSlideIdx]?.name}</p>
                            <p className="text-[9px] text-white/30 mt-0.5">{localSlides[previewSlideIdx]?.type === 'video' ? 'Video' : 'Afbeelding'} · Slide {previewSlideIdx + 1} van {localSlides.length}</p>
                          </div>
                        </div>

                        {/* Actions for selected slide */}
                        <div className="flex gap-2">
                          <button onClick={() => reorderSlide(previewSlideIdx, previewSlideIdx - 1)} disabled={previewSlideIdx === 0}
                            className="h-8 px-3 text-[10px] font-medium bg-white/[0.06] hover:bg-white/[0.10] rounded-lg border border-white/[0.06] transition-all disabled:opacity-20 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                            Eerder
                          </button>
                          <button onClick={() => reorderSlide(previewSlideIdx, previewSlideIdx + 1)} disabled={previewSlideIdx >= localSlides.length - 1}
                            className="h-8 px-3 text-[10px] font-medium bg-white/[0.06] hover:bg-white/[0.10] rounded-lg border border-white/[0.06] transition-all disabled:opacity-20 flex items-center gap-1">
                            Later
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                          </button>
                          <button onClick={() => { removeSlide(previewSlideIdx); setPreviewSlideIdx((i) => Math.max(0, Math.min(i, localSlides.length - 2))); }}
                            className="ml-auto h-8 px-3 text-[10px] font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg border border-red-500/10 transition-colors flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            Verwijder
                          </button>
                        </div>
                      </div>

                      {/* Thumbnail strip */}
                      <div className="w-[120px] shrink-0 space-y-1.5 max-h-[340px] overflow-y-auto pr-1 custom-scrollbar">
                        {localSlides.map((s, i) => (
                          <button key={`sth-${i}`} onClick={() => setPreviewSlideIdx(i)}
                            className={`w-full rounded-lg overflow-hidden border-2 transition-all ${
                              i === previewSlideIdx
                                ? 'border-white/40 ring-1 ring-white/20'
                                : 'border-white/[0.06] hover:border-white/[0.15]'
                            }`}>
                            <div className="relative aspect-video">
                              {s.type === 'video' ? (
                                <div className="w-full h-full bg-white/[0.03] flex items-center justify-center">
                                  <svg className="w-4 h-4 text-white/30" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                </div>
                              ) : (
                                <img src={s.url} alt="" className="w-full h-full object-cover" />
                              )}
                              <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[7px] text-white/50 text-center py-0.5 truncate px-1">{i + 1}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 rounded-2xl border-2 border-dashed border-white/[0.08]">
                      <span className="text-4xl opacity-15 mb-3">▶</span>
                      <p className="text-[13px] text-white/30 font-medium mb-1">Nog geen slides</p>
                      <p className="text-[10px] text-white/20 mb-4">Upload afbeeldingen of video clips om te beginnen</p>
                      <button onClick={() => slideInputRef.current?.click()} disabled={uploadingSlides}
                        className="h-9 px-5 text-[11px] font-semibold bg-white/[0.10] hover:bg-white/[0.16] rounded-xl border border-white/[0.08] transition-all disabled:opacity-40">
                        {uploadingSlides ? 'Uploaden...' : 'Bestanden kiezen'}
                      </button>
                    </div>
                  )}

                  {/* ── Templates ─────────────────────────────────────── */}
                  <div className="pt-4 border-t border-white/[0.06] space-y-3">
                    <p className="text-[12px] font-semibold text-white/60">Templates</p>

                    {/* Save current as template */}
                    {localSlides.length > 0 && (
                      <div className="flex gap-2">
                        <input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)}
                          placeholder="Template naam..."
                          className="flex-1 h-9 px-3 text-[11px] bg-white/[0.05] border border-white/[0.08] rounded-xl text-white placeholder:text-white/20 outline-none focus:border-white/[0.15] transition-colors" />
                        <button onClick={saveAsTemplate} disabled={savingTemplate || !templateName.trim()}
                          className="h-9 px-4 text-[11px] font-semibold bg-white/[0.08] hover:bg-white/[0.14] rounded-xl border border-white/[0.08] transition-all disabled:opacity-30">
                          {savingTemplate ? 'Opslaan...' : 'Opslaan als template'}
                        </button>
                      </div>
                    )}

                    {/* Template list */}
                    {slideTemplates.length > 0 ? (
          <div className="space-y-1.5">
                        {slideTemplates.map((t) => (
                          <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05] hover:bg-white/[0.06] transition-all group">
                            {/* Mini-preview: first 3 slides */}
                            <div className="flex -space-x-2 shrink-0">
                              {t.slides.slice(0, 3).map((s, j) => (
                                <div key={j} className="w-8 h-6 rounded border border-white/10 overflow-hidden bg-white/[0.03]">
                                  {s.type === 'video' ? (
                                    <div className="w-full h-full flex items-center justify-center"><svg className="w-2.5 h-2.5 text-white/30" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg></div>
                                  ) : (
                                    <img src={s.url} alt="" className="w-full h-full object-cover" />
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-medium text-white/70 truncate">{t.name}</p>
                              <p className="text-[9px] text-white/30">{t.slides.length} slide{t.slides.length !== 1 ? 's' : ''}</p>
                            </div>
                            <button onClick={() => loadTemplate(t)}
                              className="h-7 px-3 text-[10px] font-semibold bg-white/[0.08] hover:bg-white/[0.14] rounded-lg border border-white/[0.06] transition-all opacity-0 group-hover:opacity-100">
                              Laden
                            </button>
                            <button onClick={() => deleteTemplate(t.id)}
                              className="h-7 px-2 text-[10px] text-red-400/60 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100">
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-white/20 py-2">Geen opgeslagen templates. Sla de huidige slides op als template.</p>
                    )}
                  </div>
                </div>
              )}

              {settingsTab === 'timer' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-[15px] font-semibold text-white mb-1">Meeting tijdstip</h3>
                    <p className="text-[11px] text-white/40">Stel het starttijdstip in. De countdown berekent automatisch hoelang er nog afgeteld moet worden.</p>
                  </div>

                  {/* Time picker */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Starttijd</span>
                      {/* 24h / AM-PM toggle — altijd zichtbaar als pill-knop */}
                      <div className="flex items-center gap-1 p-0.5 rounded-lg bg-white/[0.06] border border-white/[0.08]">
                        <button
                          onClick={() => setUse12h(false)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                            !use12h ? 'bg-white/[0.18] text-white shadow-sm' : 'text-white/35 hover:text-white/60'
                          }`}
                        >24u</button>
                        <button
                          onClick={() => setUse12h(true)}
                          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                            use12h ? 'bg-white/[0.18] text-white shadow-sm' : 'text-white/35 hover:text-white/60'
                          }`}
                        >AM/PM</button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={use12h ? (meetingTimeHour % 12 === 0 ? 12 : meetingTimeHour % 12) : meetingTimeHour}
                        onChange={(e) => {
                          let v = Number(e.target.value);
                          if (use12h) {
                            const isPm = meetingTimeHour >= 12;
                            v = v === 12 ? (isPm ? 12 : 0) : (isPm ? v + 12 : v);
                          }
                          setMeetingTimeHour(v); saveOverlay({ meetingTimeHour: v });
                        }}
                        className="h-14 w-20 text-center text-[24px] font-bold text-white bg-white/[0.06] border border-white/[0.1] rounded-xl focus:border-white/25 focus:outline-none transition-colors appearance-none cursor-pointer">
                        {use12h
                          ? [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => (
                              <option key={i} value={i} className="bg-gray-900 text-white">{String(i).padStart(2, '0')}</option>
                            ))
                          : Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i} className="bg-gray-900 text-white">{String(i).padStart(2, '0')}</option>
                            ))
                        }
                      </select>
                      <span className="text-[28px] font-bold text-white/40">:</span>
                      <select value={meetingTimeMinute}
                        onChange={(e) => { const v = Number(e.target.value); setMeetingTimeMinute(v); saveOverlay({ meetingTimeMinute: v }); }}
                        className="h-14 w-20 text-center text-[24px] font-bold text-white bg-white/[0.06] border border-white/[0.1] rounded-xl focus:border-white/25 focus:outline-none transition-colors appearance-none cursor-pointer">
                        {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                          <option key={m} value={m} className="bg-gray-900 text-white">{String(m).padStart(2, '0')}</option>
                        ))}
                      </select>
                      {use12h ? (
                        <button
                          onClick={() => {
                            const newH = meetingTimeHour < 12 ? meetingTimeHour + 12 : meetingTimeHour - 12;
                            setMeetingTimeHour(newH); saveOverlay({ meetingTimeHour: newH });
                          }}
                          className="h-14 w-16 rounded-xl border border-white/[0.1] bg-white/[0.06] text-white font-bold text-[13px] hover:bg-white/[0.1] transition-colors"
                        >
                          {meetingTimeHour < 12 ? 'AM' : 'PM'}
                        </button>
                      ) : (
                        <span className="text-[13px] text-white/30 ml-2">uur</span>
                      )}
                    </div>
                  </div>

                  {/* Quick time presets */}
                  <div className="space-y-2">
                    <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Snelkeuze</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {[
                        [12, 0], [13, 0], [14, 0], [15, 0], [16, 0], [17, 0],
                        [18, 0], [19, 0], [20, 0], [21, 0], [22, 0], [23, 0], [0, 0],
                      ].map(([h, m]) => {
                        const active = meetingTimeHour === h && meetingTimeMinute === m;
                        const label = use12h
                          ? `${h === 0 ? 12 : h > 12 ? h - 12 : h}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
                          : `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                        return (
                          <button key={`${h}:${m}`} onClick={() => {
                            setMeetingTimeHour(h); setMeetingTimeMinute(m);
                            saveOverlay({ meetingTimeHour: h, meetingTimeMinute: m });
                          }}
                            className={`h-9 px-3 rounded-xl border-2 text-[11px] font-semibold tabular-nums transition-all ${
                              active ? 'border-white/30 bg-white/[0.1] text-white' : 'border-white/[0.06] text-white/40 hover:border-white/[0.15] hover:text-white/60'
                            }`}>
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Auto-countdown preview */}
                  <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/[0.06] space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Countdown</span>
                      <span className="text-[10px] text-white/30">{hostTimezone.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center justify-center gap-1">
                      {(() => {
                        const h = Math.floor(autoCountdownSec / 3600);
                        const m = Math.floor((autoCountdownSec % 3600) / 60);
                        const s = autoCountdownSec % 60;
                        return (
                          <>
                            {h > 0 && <span className="text-[32px] font-bold text-white tabular-nums">{h}<span className="text-[14px] text-white/30 ml-0.5">u</span></span>}
                            <span className="text-[32px] font-bold text-white tabular-nums">{String(m).padStart(2, '0')}<span className="text-[14px] text-white/30 ml-0.5">m</span></span>
                            <span className="text-[32px] font-bold text-white/60 tabular-nums">{String(s).padStart(2, '0')}<span className="text-[14px] text-white/20 ml-0.5">s</span></span>
                          </>
                        );
                      })()}
                    </div>
                    <p className="text-[10px] text-white/25 text-center">
                      Meeting om {String(meetingTimeHour).padStart(2, '0')}:{String(meetingTimeMinute).padStart(2, '0')} · telt live af
                    </p>
                  </div>

                  {/* World timezone preview */}
                  <div className="space-y-2">
                    <span className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Tijdzones</span>
                    <div className="grid grid-cols-2 gap-1.5">
                      {WORLD_ZONES.slice(0, 6).map(({ tz, label }) => {
                        const isUser = tz === userTimezone;
                        const isHost = tz === hostTimezone;
                        return (
                          <div key={tz} className={`flex items-center justify-between px-3 py-2 rounded-xl text-[11px] ${
                            isUser || isHost ? 'bg-white/[0.08] border border-white/[0.12]' : 'bg-white/[0.02] border border-white/[0.04]'
                          }`}>
                            <span className={`font-medium ${isUser || isHost ? 'text-white' : 'text-white/40'}`}>
                              {label} {isHost && '★'}
                            </span>
                            <span className={`font-mono tabular-nums ${isUser || isHost ? 'text-white' : 'text-white/30'}`}>
                              {getMeetingTimeInZone(tz)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {state === 'idle' && (
                    <p className="text-[10px] text-white/25 text-center">De countdown start wanneer je op &ldquo;Start countdown&rdquo; drukt en telt automatisch af naar het ingestelde tijdstip.</p>
                  )}
                </div>
              )}

              {settingsTab === 'widgets' && (
              <div className="space-y-5">
                <div>
                  <h3 className="text-[15px] font-semibold text-white mb-1">Meeting-widgets</h3>
                    <p className="text-[11px] text-white/40">
                      Rechtsonder in beeld tijdens de video: volgende clubbijeenkomst en optioneel RSVP tijdens het Afsluiting-blok.
                      Vereist dat er een toekomstige afspraak in de clubagenda staat.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={async () => {
                        const next = !widgetNextMeetingEnabled;
                        setWidgetNextMeetingEnabled(next);
                        await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), {
                          widgetNextMeetingEnabled: next,
                          updatedAt: serverTimestamp(),
                        }, { merge: true }).catch(() => {});
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-xl text-left transition-all ${
                        widgetNextMeetingEnabled ? 'bg-white/[0.06]' : 'bg-white/[0.02] opacity-70'
                      } hover:bg-white/[0.08] border border-white/[0.06]`}
                    >
                      <div>
                        <p className="text-[13px] font-semibold text-white">Volgende meeting tonen</p>
                        <p className="text-[10px] text-white/30 mt-0.5">Compacte of grote kaart met datum en tijd</p>
                      </div>
                      <div className={`w-10 h-[22px] rounded-full transition-colors relative shrink-0 ${widgetNextMeetingEnabled ? 'bg-emerald-500' : 'bg-white/[0.10]'}`}>
                        <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${widgetNextMeetingEnabled ? 'left-[22px]' : 'left-[3px]'}`} />
                      </div>
                    </button>

                    {widgetNextMeetingEnabled && (
                      <div className="pl-1 space-y-2">
                        <p className="text-[10px] font-medium text-white/40 uppercase tracking-wider">Formaat</p>
            <div className="flex gap-2">
                          {(['compact', 'large'] as const).map((sz) => (
              <button
                              key={sz}
                              type="button"
                              onClick={async () => {
                                setWidgetNextMeetingSize(sz);
                                await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), {
                                  widgetNextMeetingSize: sz,
                                  updatedAt: serverTimestamp(),
                                }, { merge: true }).catch(() => {});
                              }}
                              className={`flex-1 h-9 text-[11px] font-semibold rounded-xl transition-colors ${
                                widgetNextMeetingSize === sz ? 'bg-violet-500 text-white' : 'bg-white/[0.06] text-white/45 hover:bg-white/[0.10]'
                              }`}
                            >
                              {sz === 'compact' ? 'Compact' : 'Groot'}
              </button>
                          ))}
            </div>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={async () => {
                        const next = !widgetRsvpClosingEnabled;
                        setWidgetRsvpClosingEnabled(next);
                        await setDoc(doc(db, COLLECTIONS.SETTINGS, entityId), {
                          widgetRsvpClosingEnabled: next,
                          updatedAt: serverTimestamp(),
                        }, { merge: true }).catch(() => {});
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-xl text-left transition-all ${
                        widgetRsvpClosingEnabled ? 'bg-white/[0.06]' : 'bg-white/[0.02] opacity-70'
                      } hover:bg-white/[0.08] border border-white/[0.06]`}
                    >
                      <div>
                        <p className="text-[13px] font-semibold text-white">RSVP tijdens afsluiting</p>
                        <p className="text-[10px] text-white/30 mt-0.5">Ja/Nee-prompt in het Afsluiting-blok; koppelt aan club-RSVP</p>
                      </div>
                      <div className={`w-10 h-[22px] rounded-full transition-colors relative shrink-0 ${widgetRsvpClosingEnabled ? 'bg-emerald-500' : 'bg-white/[0.10]'}`}>
                        <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${widgetRsvpClosingEnabled ? 'left-[22px]' : 'left-[3px]'}`} />
                      </div>
                    </button>
          </div>

                  <p className="text-[10px] text-white/25 leading-relaxed">
                    Instellingen worden opgeslagen in meeting-instellingen en zijn meteen zichtbaar voor iedereen in de meeting.
                  </p>
                </div>
              )}

              {settingsTab === 'sessie' && (
              <div className="space-y-3 min-h-0">
                <div>
                  <h3 className="text-[15px] font-semibold text-white mb-1">Meeting instellingen</h3>
                    <p className="text-[11px] text-white/40">
                      Sessietemplate en blokken · dezelfde editor als in de club-hub.
                    </p>
                  </div>
                  {!sessionTemplateEditor ? (
                    <p className="text-[12px] text-white/50 leading-relaxed rounded-xl border border-white/[0.08] bg-white/[0.04] px-4 py-3">
                      Open de countdown vanuit een actieve netwerkclub-meeting als <strong className="text-white/70">host</strong> om
                      leden en sessiestructuur te kunnen bewerken.
                    </p>
                  ) : (
                    <>
                      {sessionTemplateSavedFlash && (
                        <p className="text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-3 py-2">
                          Sessiestructuur opgeslagen. Geldt voor de volgende meeting.
                        </p>
                      )}
                      {/* Dark-mode wrapper: overschrijft witte Tailwind styling van PreMeetingSetupPanel */}
                      <style>{`
                        /* Achtergronden — solid donker, geen witte glans */
                        .mm-dark-panel .bg-white   { background-color: #1e2228 !important; }
                        .mm-dark-panel .bg-gray-50 { background-color: #181c21 !important; }
                        .mm-dark-panel .bg-gray-100{ background-color: #22272f !important; }
                        .mm-dark-panel .bg-gradient-to-b {
                          background-image: none !important;
                          background-color: #181c21 !important;
                        }
                        /* Borders */
                        .mm-dark-panel .border-gray-100,
                        .mm-dark-panel .border-gray-200 { border-color: rgba(255,255,255,0.10) !important; }
                        /* Tekst */
                        .mm-dark-panel .text-gray-900,
                        .mm-dark-panel .text-gray-800 { color: rgba(255,255,255,0.88) !important; }
                        .mm-dark-panel .text-gray-700 { color: rgba(255,255,255,0.72) !important; }
                        .mm-dark-panel .text-gray-600 { color: rgba(255,255,255,0.58) !important; }
                        .mm-dark-panel .text-gray-500 { color: rgba(255,255,255,0.44) !important; }
                        .mm-dark-panel .text-gray-400 { color: rgba(255,255,255,0.34) !important; }
                        .mm-dark-panel .text-gray-300 { color: rgba(255,255,255,0.26) !important; }
                        /* Formuliervelden */
                        .mm-dark-panel input,
                        .mm-dark-panel textarea,
                        .mm-dark-panel select {
                          background: rgba(255,255,255,0.07) !important;
                          border-color: rgba(255,255,255,0.12) !important;
                          color: rgba(255,255,255,0.85) !important;
                        }
                        .mm-dark-panel input::placeholder,
                        .mm-dark-panel textarea::placeholder { color: rgba(255,255,255,0.25) !important; }
                        /* Knoppen met lichte border */
                        .mm-dark-panel button.border-gray-200,
                        .mm-dark-panel .border-gray-200 {
                          background: rgba(255,255,255,0.05) !important;
                          border-color: rgba(255,255,255,0.12) !important;
                          color: rgba(255,255,255,0.65) !important;
                        }
                        /* Schaduw verwijderen (ziet er slecht uit op donker) */
                        .mm-dark-panel .shadow-sm,
                        .mm-dark-panel .shadow-md { box-shadow: none !important; }
                        /* Groene accenten */
                        .mm-dark-panel .bg-emerald-50    { background: rgba(52,211,153,0.10) !important; }
                        .mm-dark-panel .text-emerald-700 { color: #6EE7B7 !important; }
                        .mm-dark-panel .text-emerald-600 { color: #34D399 !important; }
                        .mm-dark-panel .border-emerald-100 { border-color: rgba(52,211,153,0.15) !important; }
                        .mm-dark-panel .border-emerald-200 { border-color: rgba(52,211,153,0.28) !important; }
                        /* Gestreepte rand (onassigned leden) */
                        .mm-dark-panel .border-dashed    { border-color: rgba(52,211,153,0.32) !important; }
                        .mm-dark-panel .hover\\:bg-emerald-50:hover { background: rgba(52,211,153,0.10) !important; }
                        /* Witte ring om avatars */
                        .mm-dark-panel .ring-2 { --tw-ring-color: rgba(255,255,255,0.15) !important; }
                      `}</style>
                      <div className="mm-dark-panel" style={{ background: 'transparent', borderRadius: 14, overflow: 'hidden' }}>
                        <PreMeetingSetupPanel
                          variant="embedded"
                          entityId={entityId}
                          entityType={sessionTemplateEditor.entityType}
                          userId={sessionTemplateEditor.userId}
                          memberCount={sessionTemplateEditor.memberCount}
                          memberNames={sessionTemplateEditor.memberNames}
                          memberAvatars={sessionTemplateEditor.memberAvatars}
                          onSaved={() => {
                            setSessionTemplateSavedFlash(true);
                            window.setTimeout(() => setSessionTemplateSavedFlash(false), 4000);
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}

              {settingsTab === 'features' && featureToggles && onFeatureToggle && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-[15px] font-semibold text-white mb-1">Meeting features</h3>
                    <p className="text-[11px] text-white/40">Schakel functies aan of uit voor deze meeting. Uitgeschakelde features zijn verborgen voor alle deelnemers.</p>
                  </div>

                  <div className="space-y-1">
                    {([
                      { key: 'docView'          as const, label: 'DocumentView',     desc: 'Gedeeld scherm · bestanden en websites delen met deelnemers', Icon: DocumentIcon },
                      { key: 'linkOpener'       as const, label: 'Link opener',       desc: "Website URL's live openen in de meeting voor alle deelnemers", Icon: ConnectionLinkIcon },
                      { key: 'guestSpotlight'   as const, label: 'Gasten Spotlight',  desc: 'QR-code aanmelding en presentatieslots voor gasten',           Icon: MicrophoneIcon },
                      { key: 'commitmentReview' as const, label: 'Commitment Review', desc: 'Openstaande acties per lid bekijken en afvinken tijdens de meeting', Icon: GoalIcon },
                      { key: 'slideshow'        as const, label: 'Slideshow',         desc: 'Presenteer afbeeldingen en video clips tijdens de meeting',     Icon: FilmIcon },
                      { key: 'taskReminder'     as const, label: 'Taken Reminder',    desc: 'Herinner leden automatisch aan open taken. Stel de frequentie in via de opties hieronder.', Icon: NotificationIcon },
                      { key: 'autoRecord'       as const, label: 'Altijd opnemen',    desc: 'Start de opname automatisch zodra een meeting begint. De browser vraagt eenmalig toestemming.', Icon: VideoIcon },
                    ] as const).map(({ key, label, desc, Icon }) => {
                      const enabled = featureToggles[key];
                      return (
                        <button key={key} onClick={() => onFeatureToggle(key, !enabled)}
                          className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all ${
                            enabled ? 'bg-white/[0.05]' : 'bg-white/[0.01] opacity-60'
                          } hover:bg-white/[0.07]`}>
                          <Icon size={18} weight="regular" className={`shrink-0 ${enabled ? 'text-white/70' : 'text-white/30'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-[13px] font-semibold ${enabled ? 'text-white' : 'text-white/40'}`}>{label}</p>
                            <p className="text-[10px] text-white/25 mt-0.5 leading-snug">{desc}</p>
                          </div>
                          <div className={`w-10 h-[22px] rounded-full transition-colors relative shrink-0 ${enabled ? 'bg-emerald-500' : 'bg-white/[0.10]'}`}>
                            <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${enabled ? 'left-[22px]' : 'left-[3px]'}`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Reminder frequentie — alleen zichtbaar als taskReminder aan staat */}
                  {featureToggles?.taskReminder && (
                    <div className="mt-1 p-3.5 rounded-xl bg-white/[0.04] border border-white/[0.07] space-y-2.5">
                      <div>
                        <p className="text-[11px] text-white/50 font-semibold mb-0.5">Herinneringsfrequentie</p>
                        <p className="text-[11px] text-white/35 leading-relaxed">
                          Hoe vaak worden leden herinnerd aan hun openstaande taken? Leden krijgen een melding in hun inbox <em>en</em> een toast-bericht zodra ze de app openen, zodat commitments niet vergeten worden tussen twee sessies.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {([2, 4, 7] as const).map(d => (
          <button
                            key={d}
                            onClick={async () => {
                              setReminderDays(d);
                              await setDoc(doc(db, 'entitySettings', entityId), {
                                taskReminderEnabled: true,
                                taskReminderDays: d,
                                updatedAt: serverTimestamp(),
                              }, { merge: true }).catch(() => {});
                            }}
                            className={`flex-1 h-8 text-[11px] font-semibold rounded-lg transition-colors ${reminderDays === d ? 'bg-amber-500 text-white' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'}`}
                          >
                            {d === 7 ? 'Wekelijks' : `Elke ${d}d`}
          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-white/25">Tip: kies "Elke 2d" voor intensieve groepen, "Wekelijks" voor rustigere trajecten.</p>
        </div>
      )}

                  <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
                    <p className="text-[10px] text-white/25 leading-relaxed">
                      Wijzigingen worden direct opgeslagen en gesynchroniseerd naar alle deelnemers. Alleen organisatoren en boardleden kunnen features beheren.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes settingsFadeIn {
          from { opacity: 0; transform: scale(0.97) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* Overlay content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-lg">
        {clubName && (
          <p className="text-sm uppercase tracking-widest opacity-70 mb-3">{clubName}</p>
        )}

        {state === 'running' && remainingSeconds > 0 ? (
          <>
            <p className="text-xl font-semibold opacity-80 mb-2">{label}</p>
            <div className="text-[80px] sm:text-[120px] font-black leading-none tabular-nums" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}>
              {minutes > 0
                ? `${minutes}:${String(seconds).padStart(2, '0')}`
                : `${remainingSeconds}s`}
            </div>
            {STRINGS.countdown.sub && <p className="mt-4 text-sm opacity-60 max-w-xs">{STRINGS.countdown.sub}</p>}
          </>
        ) : state === 'finished' ? (
          <div className="flex flex-col items-center space-y-6">
            <p className="text-4xl font-black">We beginnen!</p>
          </div>
        ) : (
          <p className="text-3xl font-bold mb-2" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.4)' }}>{clubName ?? 'Welkom!'}</p>
        )}

        {musicError && (
          <p role="alert" className="mt-3 text-xs opacity-70 bg-white/10 rounded-lg px-3 py-2">
            {musicError}
          </p>
        )}
        {videoError && (
          <p role="alert" className="mt-2 text-xs opacity-70 bg-white/10 rounded-lg px-3 py-2">
            {videoError}
          </p>
        )}

        {/* Host controls */}
        {isHost && (
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {canRunCountdown && state === 'idle' && (
              <>
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="min-h-[48px] px-5 rounded-xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 transition-all active:scale-95 text-sm"
                >
                  Instellingen
                </button>
                <button
                  onClick={() => void handleStart()}
                  disabled={isMusicLoading}
                  aria-busy={isMusicLoading}
                  className="min-h-[48px] px-6 rounded-xl font-semibold bg-white transition-all disabled:opacity-50 active:scale-95"
                  style={{ color: primaryColor }}
                >
                  {isMusicLoading ? 'Muziek laden...' : STRINGS.countdown.startButton}
                </button>
              </>
            )}
            {canRunCountdown && state === 'running' && (
              <button
                onClick={() => void handleStop()}
                className="min-h-[48px] px-6 rounded-xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 transition-all active:scale-95"
              >
                {STRINGS.countdown.stopButton}
              </button>
            )}
            {canRunCountdown && (
              <button
                onClick={() => void handleMeetingStart()}
                className="min-h-[48px] px-6 rounded-xl font-black text-white transition-all active:scale-95"
                style={{ backgroundColor: '#8DA8A0' }}
              >
                {startLabel ?? 'Meeting starten \u2192'}
              </button>
            )}
            {!canRunCountdown && state === 'idle' && (
              <>
                {!showSettings && (
                  <button
                    type="button"
                    onClick={() => { setSettingsTab('sessie'); setShowSettings(true); }}
                    className="min-h-[48px] px-5 rounded-xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 transition-all active:scale-95 text-sm"
                  >
                    Instellingen
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="min-h-[48px] px-6 rounded-xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 transition-all active:scale-95 text-sm"
                >
                  Klaar
                </button>
              </>
            )}
          </div>
        )}
        {!isHost && state === 'running' && (
          <div className="mt-8 flex flex-col items-center gap-3 max-w-sm mx-auto px-4">
            <p className="text-xs text-white/55 text-center leading-relaxed">
              De countdown loopt door op de achtergrond. Je kunt dit scherm sluiten en later weer openen via &quot;Countdown meekijken&quot; in de meeting.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="min-h-[44px] px-6 rounded-xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 transition-all text-sm"
            >
              Sluiten (meekijken gestopt)
            </button>
          </div>
        )}
        {!isHost && (state === 'idle' || state === 'finished') && (
          <div className="flex gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="min-h-[52px] px-7 rounded-2xl font-semibold bg-white/15 hover:bg-white/25 border border-white/30 transition-all active:scale-95 text-base"
            >
              Afwijzen
            </button>
            <button
              type="button"
              onClick={onMeetingStart}
              className="min-h-[52px] px-8 rounded-2xl font-black text-white transition-all active:scale-95 text-base shadow-lg"
              style={{ backgroundColor: '#f97316' }}
            >
              Join nu →
            </button>
          </div>
        )}
      </div>

      {/* ── Spectrum analyser ── */}
      <canvas
        ref={canvasRef}
        className="absolute bottom-0 left-0 w-full pointer-events-none z-[6]"
        style={{
          height: '140px',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 38%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 38%)',
        }}
      />
    </div>
  );
};

export default MeetingCountdown;
